/**
 * Underrated: 30-minute "extra 5% off" offer controller.
 *
 * Loaded synchronously in <head> on every storefront page (see snippets/product-discount-countdown.liquid)
 * so the offer state is written to <html> before first paint. One controller per document.
 *
 * States (html[data-ur-countdown]):
 *   none     - timer never started (or storage unusable): no countdown, cart panel locked
 *   active   - timer running: countdown shown, cart panel locked
 *   unlocked - a confirmed add-to-cart succeeded before expiry in this browser session: panel unlocked
 *   expired  - timer ended without qualifying: countdown hidden (or neutral message), panel locked
 *
 * html[data-ur-offer="unlocked"] is the only thing that reveals the existing EXTRA5 cart panel
 * (CSS in assets/product-discount-countdown.css; click guard in assets/cart-promo-offer.js).
 * This is a client-side presentation gate, not server-side discount enforcement.
 *
 * Persistence:
 *   localStorage ur_cd_timer_v1  {v, startedAt, expiresAt}   absolute expiry, never reset or restarted
 *   localStorage ur_cd_unlock_v1 {v, sid, unlockedAt}        valid only while sid matches the cookie
 *   cookie       ur_cd_sid                                   session cookie shared by all tabs
 */
(function () {
  'use strict';

  var TIMER_KEY = 'ur_cd_timer_v1';
  var UNLOCK_KEY = 'ur_cd_unlock_v1';
  var SID_COOKIE = 'ur_cd_sid';
  var LINES_UPDATE_EVENT = 'shopify:cart:lines-update';
  var MAX_DURATION_MS = 24 * 60 * 60 * 1000;
  var CLOCK_SKEW_MS = 5 * 60 * 1000;

  var win = window;
  var doc = document;
  if (win.URDiscountCountdown) return;

  var config = readConfig();
  var root = doc.documentElement;
  var storage = getStorage();
  var sid = config.enabled ? ensureSessionId() : null;

  /** The record this document believes in; used to converge concurrent tabs on the earliest expiry. */
  var known = null;
  var currentState = null;
  var ticker = null;

  function readConfig() {
    var defaults = { enabled: false, startHere: false, durationMinutes: 30, liveUnlocked: '', liveExpired: '' };
    try {
      var el = doc.getElementById('ur-cd-config');
      var parsed = el ? JSON.parse(el.textContent || '{}') : {};
      for (var key in parsed) defaults[key] = parsed[key];
    } catch (error) {
      // Malformed config: stay disabled rather than guessing.
    }
    var minutes = Number(defaults.durationMinutes);
    defaults.durationMs = Math.min(MAX_DURATION_MS, Math.max(1, isFinite(minutes) ? minutes : 30) * 60000);
    return defaults;
  }

  function getStorage() {
    try {
      var probe = '__ur_cd_probe__';
      win.localStorage.setItem(probe, '1');
      win.localStorage.removeItem(probe);
      return win.localStorage;
    } catch (error) {
      return null;
    }
  }

  function readCookie(name) {
    try {
      var match = doc.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]+)'));
      return match ? decodeURIComponent(match[1]) : null;
    } catch (error) {
      return null;
    }
  }

  function randomId() {
    var bytes = new Uint8Array(12);
    if (win.crypto && win.crypto.getRandomValues) {
      win.crypto.getRandomValues(bytes);
    } else {
      for (var i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
    }
    var out = '';
    for (var j = 0; j < bytes.length; j++) out += ('0' + bytes[j].toString(16)).slice(-2);
    return out;
  }

  /** Session cookie (no Expires/Max-Age): shared by every tab, cleared when the browser session ends. */
  function ensureSessionId() {
    var existing = readCookie(SID_COOKIE);
    if (existing) return existing;
    var id = randomId();
    try {
      var secure = win.location && win.location.protocol === 'https:' ? '; Secure' : '';
      doc.cookie = SID_COOKIE + '=' + id + '; path=/; SameSite=Lax' + secure;
    } catch (error) {
      return null;
    }
    // Cookies blocked: no shared session, so unlocking is not possible (fail closed).
    return readCookie(SID_COOKIE) === id ? id : null;
  }

  /** @returns {{ status: 'missing' | 'valid' | 'corrupt' | 'unavailable', record?: {startedAt: number, expiresAt: number} }} */
  function readTimer() {
    if (!storage) return { status: 'unavailable' };
    var raw;
    try {
      raw = storage.getItem(TIMER_KEY);
    } catch (error) {
      return { status: 'unavailable' };
    }
    if (raw === null) return { status: 'missing' };
    var record = parseTimer(raw);
    return record ? { status: 'valid', record: record } : { status: 'corrupt' };
  }

  function parseTimer(raw) {
    try {
      var data = JSON.parse(raw);
      var startedAt = Number(data && data.startedAt);
      var expiresAt = Number(data && data.expiresAt);
      if (!isFinite(startedAt) || !isFinite(expiresAt)) return null;
      if (expiresAt <= startedAt || expiresAt - startedAt > MAX_DURATION_MS) return null;
      if (startedAt > Date.now() + CLOCK_SKEW_MS) return null;
      return { startedAt: startedAt, expiresAt: expiresAt };
    } catch (error) {
      return null;
    }
  }

  function writeTimer(record) {
    try {
      storage.setItem(TIMER_KEY, JSON.stringify({ v: 1, startedAt: record.startedAt, expiresAt: record.expiresAt }));
      return true;
    } catch (error) {
      return false;
    }
  }

  function readUnlock() {
    if (!storage || !sid) return null;
    try {
      var data = JSON.parse(storage.getItem(UNLOCK_KEY) || 'null');
      if (!data || data.sid !== sid || !isFinite(Number(data.unlockedAt))) return null;
      return data;
    } catch (error) {
      return null;
    }
  }

  /** Starts the timer once, on a real product page, only if no record (valid, expired or corrupt) exists. */
  function startIfNeeded() {
    if (!config.enabled || !config.startHere) return;
    var timer = readTimer();
    if (timer.status !== 'missing') return;
    var now = Date.now();
    var record = { startedAt: now, expiresAt: now + config.durationMs };
    if (writeTimer(record)) known = record;
  }

  /**
   * Keeps every tab on the earliest legitimate expiry. If another tab's concurrent start overwrote an
   * earlier value, write the earlier one back; otherwise adopt what is stored. Never extends an expiry.
   */
  function reconcile() {
    var timer = readTimer();
    if (timer.status === 'valid') {
      if (known && known.expiresAt < timer.record.expiresAt) {
        writeTimer(known);
      } else {
        known = timer.record;
      }
    } else if (timer.status === 'missing' && known) {
      // Our record disappeared while this page was open; restore it rather than restarting.
      writeTimer(known);
    }
    return timer;
  }

  function computeState() {
    if (!config.enabled) return 'none';
    if (readUnlock()) return 'unlocked';
    var timer = readTimer();
    if (timer.status !== 'valid') return 'none';
    var expiresAt = Math.min(timer.record.expiresAt, known ? known.expiresAt : Infinity);
    return Date.now() < expiresAt ? 'active' : 'expired';
  }

  function remainingMs() {
    var timer = readTimer();
    if (timer.status !== 'valid') return 0;
    var expiresAt = Math.min(timer.record.expiresAt, known ? known.expiresAt : Infinity);
    return Math.max(0, expiresAt - Date.now());
  }

  function format(ms) {
    var total = Math.ceil(ms / 1000);
    var minutes = Math.floor(total / 60);
    var seconds = total % 60;
    return (minutes < 10 ? '0' : '') + minutes + ':' + (seconds < 10 ? '0' : '') + seconds;
  }

  /** Writes the remaining time into every countdown on the page (they may be re-rendered by morphs). */
  function paint() {
    var state = currentState || computeState();
    var nodes = doc.querySelectorAll('[data-ur-cd-time]');
    if (!nodes.length) return;
    var text = state === 'active' ? format(remainingMs()) : '00:00';
    var minutesLeft = Math.ceil(remainingMs() / 60000);
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].textContent !== text) nodes[i].textContent = text;
    }
    var srNodes = doc.querySelectorAll('[data-ur-cd-remaining]');
    for (var j = 0; j < srNodes.length; j++) {
      var label = state === 'active' ? 'About ' + minutesLeft + (minutesLeft === 1 ? ' minute' : ' minutes') + ' left.' : '';
      // Not a live region: updated at most once a minute and never announced.
      if (srNodes[j].textContent !== label) srNodes[j].textContent = label;
    }
  }

  function announce(message) {
    if (!message) return;
    var regions = doc.querySelectorAll('[data-ur-cd-live]');
    for (var i = 0; i < regions.length; i++) regions[i].textContent = message;
  }

  function apply(state) {
    var previous = currentState;
    currentState = state;
    if (root.getAttribute('data-ur-countdown') !== state) root.setAttribute('data-ur-countdown', state);
    if (state === 'unlocked') {
      root.setAttribute('data-ur-offer', 'unlocked');
    } else if (root.hasAttribute('data-ur-offer')) {
      root.removeAttribute('data-ur-offer');
    }
    paint();

    if (previous && previous !== state) {
      if (state === 'unlocked') announce(config.liveUnlocked);
      else if (state === 'expired') announce(config.liveExpired);
    }

    if (state === 'active') {
      if (!ticker) ticker = win.setInterval(tick, 1000);
    } else if (ticker) {
      win.clearInterval(ticker);
      ticker = null;
    }
  }

  function tick() {
    reconcile();
    apply(computeState());
  }

  /**
   * Unlocks only on a confirmed successful add: product-form.js resolves the event promise with
   * detail.didError === false after the server accepted the add (single and batch paths). Optimistic
   * dispatches, failures (didError true / rejection), updates and removals never unlock.
   * Boundary: the add qualifies only if that confirmation arrives strictly before expiry.
   */
  function onLinesUpdate(event) {
    if (!event || event.action !== 'add' || !event.promise || typeof event.promise.then !== 'function') return;
    event.promise.then(
      function (result) {
        if (!result || !result.detail || result.detail.didError !== false) return;
        qualify();
      },
      function () {}
    );
  }

  function qualify() {
    if (!config.enabled || !storage || !sid) return false;
    reconcile();
    if (computeState() !== 'active') {
      apply(computeState());
      return false;
    }
    try {
      storage.setItem(UNLOCK_KEY, JSON.stringify({ v: 1, sid: sid, unlockedAt: Date.now() }));
    } catch (error) {
      return false;
    }
    apply(computeState());
    return true;
  }

  function onStorage(event) {
    if (event && event.key !== null && event.key !== TIMER_KEY && event.key !== UNLOCK_KEY) return;
    tick();
  }

  function onVisibility() {
    if (doc.visibilityState === 'visible') tick();
  }

  startIfNeeded();
  reconcile();
  apply(computeState());

  if (config.enabled) {
    doc.addEventListener(LINES_UPDATE_EVENT, onLinesUpdate);
    win.addEventListener('storage', onStorage);
    win.addEventListener('pageshow', tick);
    win.addEventListener('focus', tick);
    doc.addEventListener('visibilitychange', onVisibility);
    doc.addEventListener('DOMContentLoaded', paint);
    doc.addEventListener('shopify:section:load', paint);
  }

  win.URDiscountCountdown = {
    paint: paint,
    getState: function () {
      return currentState;
    },
    /** Test hook; not used by the theme. */
    _debug: function () {
      return { state: currentState, sid: sid, storage: !!storage, known: known, timer: readTimer(), unlock: readUnlock() };
    },
  };
})();
